import { Octokit } from '@octokit/rest'
import { LangGraphRunnableConfig } from '@langchain/langgraph'
import { Account } from '@prisma/client'

import { GitHubFile } from './types'

export const createIssue = async (
  {
    title,
    body
  }: {
    title: string
    body?: string
  },
  config: LangGraphRunnableConfig
): Promise<void> =>
  await fetch(
    `https://api.github.com/repos/${config.configurable?.username?.toLowerCase()}/${config.configurable?.project?.name?.toLowerCase()}/issues`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${
          config.configurable?.user?.accounts?.find(
            (a: Account) => a.provider === 'github'
          )?.access_token
        }`
      },
      body: JSON.stringify({ title, body })
    }
  )
    .then(async res => await res.json())
    .catch(console.error)

export const getGitHubFileContent = async ({
  owner,
  repo,
  path,
  accessToken
}: {
  owner: string
  repo: string
  path: string
  accessToken: string
}) => {
  const octokit = new Octokit({
    auth: accessToken
  })
  const fileContent = await octokit
    .request(`GET /repos/${owner}/${repo}/contents/${path.replaceAll('"', '')}`)
    .then(({ data }) => Buffer.from(data.content, 'base64').toString('utf-8'))
    .catch(e => {
      console.error('Error reading file: ' + e)
      return e.message
    })
    
  return fileContent
}

export const getPackageJsonInRepo = async ({
  owner,
  repo,
  accessToken
}: {
  owner: string
  repo: string
  accessToken: string
}) =>
  await getGitHubFileContent({
    owner,
    repo,
    path: 'package.json',
    accessToken
  }).then(data => {
    try {
      const packageJson = JSON.parse(data)
      return packageJson
    } catch (error) {
      console.error('Error parsing package.json:', error)
      return null
    }
  })

export const getRepoFileTree = async ({
  owner,
  repo,
  accessToken,
  branch
}: {
  owner: string
  repo: string
  accessToken: string
  branch?: string
}) => {
  const octokit = new Octokit({
    auth: accessToken
  })
  let treeString = ''
  if (!branch) {
    branch = await getDefaultBranch(owner, repo, accessToken)
  }
  async function listDirectoryContents(
    owner: string,
    repo: string,
    path: string,
    indent: string = ''
  ) {
    const response = (await octokit.rest.repos
      .getContent({ owner, repo, path, ref: branch })
      .catch(e => {
        console.error('Error getting repo files:', e)
      })) ?? { data: [] }
    if (Array.isArray(response.data)) {
      const dirs = []
      const files = []
      for (const item of response.data) {
        if (item.type === 'dir') {
          dirs.push(item)
        } else if (item.type === 'file') {
          files.push(item)
        }
      }

      // Print directories first with trailing slashes
      for (const dir of dirs) {
        treeString += `${indent}├── ${dir.name}/\n`
        await listDirectoryContents(owner, repo, dir.path, indent + '│   ')
      }

      // Then print files, indented to align with the last directory entry
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        if (i === files.length - 1) {
          // If it's the last file in this level, print a special character
          treeString += `${indent}└── ${file.name} (${file.path})\n`
        } else {
          treeString += `${indent}├── ${file.name} (${file.path})\n`
        }
      }
    }
  }

  try {
    await listDirectoryContents(owner, repo, '') // Start with the root path '' for the repository itself
  } catch (error) {
    console.error('Error:', error)
  }

  return treeString.replace('├', '┌') // Replace the first occurrence for a clean start
}

export const getIssues = async (repoUrl: string, accessToken: string) => {
  const octokit = new Octokit({
    auth: accessToken
  })
  const issues = await octokit.paginate('GET /repos/{owner}/{repo}/issues', {
    owner: repoUrl.split('/')[3],
    repo: repoUrl.split('/')[4],
    per_page: 10
  })
  return issues
}

/**
 * Get the default branch for a repository
 */
const getDefaultBranch = async (
  owner: string,
  repo: string,
  token: string
): Promise<string> => {
  try {
    const octokit = new Octokit({
      auth: token
    })
    const { data } = await octokit.repos.get({
      owner,
      repo
    })

    const branch = data.default_branch
    return branch
  } catch (error) {
    console.error('Error getting default branch:', error)
    return 'main' // Fallback to main
  }
}

/**
 * Get all files from a repository
 */
export const getAllFiles = async (
  token: string,
  repoUrl: string,
  branch?: string
): Promise<GitHubFile[]> => {
  try {
    const repository = repoUrl.replace('https://github.com/', '')
    const owner = repository.split('/')[0]
    const repo = repository.split('/')[1]

    if (!branch) {
      branch = await getDefaultBranch(owner, repo, token)
    }

    const octokit = new Octokit({
      auth: token
    })

    // Get the tree recursively
    const { data } = await octokit.git.getTree({
      owner,
      repo,
      tree_sha: branch,
      recursive: '1'
    })

    // Filter out directories and get only files
    const fileItems = data.tree.filter(
      item => item.type === 'blob' && item.path
    )

    // Process files in batches to avoid rate limiting
    const batchSize = 10
    const files: GitHubFile[] = []

    for (let i = 0; i < fileItems.length; i += batchSize) {
      const batch = fileItems.slice(i, i + batchSize)
      const batchPromises = batch.map(async item => {
        try {
          if (!item.path) return null

          // Skip binary files, large files, and non-code files
          if (shouldSkipFile(item.path, item.size || 0)) {
            return null
          }

          const { data } = await octokit.repos.getContent({
            owner,
            repo,
            path: item.path,
            ref: branch
          })

          if ('content' in data && typeof data.content === 'string') {
            const content = Buffer.from(data.content, 'base64').toString(
              'utf-8'
            )
            const extension = item.path.split('.').pop() || ''

            return {
              name: item.path.split('/').pop() || '',
              path: item.path,
              content,
              size: item.size || 0,
              last_modified: new Date().toISOString(), // GitHub API doesn't provide this directly
              extension
            }
          }
          return null
        } catch (error) {
          console.error(`Error fetching file ${item.path}:`, error)
          return null
        }
      })

      const batchResults = await Promise.all(batchPromises)
      files.push(...batchResults.filter(f => f != null))

      // Add a small delay to avoid rate limiting
      if (i + batchSize < fileItems.length) {
        await new Promise(resolve => setTimeout(resolve, 1000))
      }
    }

    return files
  } catch (error) {
    throw new Error(`Failed to fetch files from GitHub: ${error}`)
  }
}

export const fetchUserGitHubRepos = async (accessToken: string) => {
  try {
    const octokit = new Octokit({
      auth: accessToken
    })

    // Fetch repositories for the authenticated user
    const response = await octokit.repos.listForAuthenticatedUser({
      visibility: 'all',
      per_page: 100
    })

    return response.data
  } catch (error) {
    console.error('Failed to fetch GitHub repositories:', error)
    throw error
  }
}

export type GitHubRepo = Awaited<
  ReturnType<typeof fetchUserGitHubRepos>
>[number]

/**
 * Determine if a file should be skipped based on extension or size
 */
const shouldSkipFile = (path: string, size: number): boolean => {
  // Skip files larger than 1MB
  if (size > 1024 * 1024) {
    return true
  }

  const extension = path.split('.').pop()?.toLowerCase() || ''
  const filename = path.split('/').pop()?.toLowerCase() || ''

  // Skip binary files and non-code files
  const skipExtensions = [
    'png',
    'jpg',
    'jpeg',
    'gif',
    'svg',
    'ico',
    'webp',
    'mp4',
    'webm',
    'mp3',
    'wav',
    'ogg',
    'pdf',
    'doc',
    'docx',
    'ppt',
    'pptx',
    'xls',
    'xlsx',
    'zip',
    'tar',
    'gz',
    'rar',
    '7z',
    'exe',
    'dll',
    'so',
    'dylib',
    'ttf',
    'otf',
    'woff',
    'woff2',
    'lock'
  ]

  // Skip certain files by name
  const skipFilenames = [
    'package-lock.json',
    'pnpm-lock.yaml',
    'yarn.lock',
    '.gitignore',
    '.ds_store'
  ]

  return skipExtensions.includes(extension) || skipFilenames.includes(filename)
}
